import { useEffect } from 'react';

import { useGraphQLSuspenseQuery } from '@betterment/graphql';
import { useAutoNavigate } from '@betterment/js-runtime';
import { Box, ContentLayoutF, H2, Text } from '@betterment/samba-react';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { BulkFeeAttempt, isInvalidFeeAttemptBatch, isValidFeeAttemptBatch } from './data';
import { BulkFeeAttemptDocument } from './graphql/query.bulkFeeAttempt.generated';
import ResolveErrorsTable from './resolve-errors-table/ResolveErrorsTable';
import { ResolveUploadCsvForm } from './resolve-upload-form/ResolveUploadCsvForm';
import { useUploadCsvSubmit } from './useUploadCsvSubmit';

import { FullPageLoader } from '../../../Shared/Components/FullPageLoader';
import urlBuilder from '../../../Shared/url-builder';

export const OnDemandBillingResolve = () => {
  const navigate = useAutoNavigate();
  const id = useRequiredParam('id');

  const { data } = useGraphQLSuspenseQuery(BulkFeeAttemptDocument, {
    variables: { id },
  });

  const handleSuccess = (bulkFeeAttempt: BulkFeeAttempt) => {
    if (!bulkFeeAttempt) {
      void navigate(urlBuilder.getOnDemandBillingStartPage.absolutePath);
      return;
    }

    if (isValidFeeAttemptBatch(bulkFeeAttempt.feeAttempts)) {
      void navigate(urlBuilder.getOnDemandBillingReviewPage.absolute({ id: bulkFeeAttempt.id }));
    }
  };

  const { isSubmittingUploadedCsv, uploadCsvValidationError, handleSubmit } = useUploadCsvSubmit({
    variant: 'update',
    id,
    onSuccess: handleSuccess,
  });

  const { bulkFeeAttempt } = data.firm;

  useEffect(() => {
    if (!bulkFeeAttempt) {
      void navigate(urlBuilder.getOnDemandBillingStartPage.absolutePath);
      return;
    }
    if (isValidFeeAttemptBatch(bulkFeeAttempt.feeAttempts)) {
      void navigate(urlBuilder.getOnDemandBillingReviewPage.absolute({ id }));
    }
  }, [bulkFeeAttempt, navigate, id]);

  if (!bulkFeeAttempt) {
    return null;
  }

  const failures = isInvalidFeeAttemptBatch(bulkFeeAttempt.feeAttempts) ? bulkFeeAttempt.feeAttempts.failures : [];

  const handleUploadSubmit = async (values: { csvFile: File | null }) => {
    await handleSubmit({ csvFile: values.csvFile });
  };

  if (isSubmittingUploadedCsv) {
    return (
      <FullPageLoader
        text={
          <>
            <Text variant="text200" css={{ fontWeight: 'medium' }}>
              Preparing for review
            </Text>
            <Text variant="text100">This may take a few minutes...</Text>
          </>
        }
      />
    );
  }

  return (
    <Box css={{ width: 'container.max', display: 'flex', flexDirection: 'column', justifySelf: 'center' }}>
      <H2 css={{ pb: 'space.400' }}>Resolve the following issue(s) and re-upload.</H2>
      <ContentLayoutF>
        <ContentLayoutF.SlotContainer>
          <ContentLayoutF.Slot1>
            <ResolveErrorsTable failures={failures} />
          </ContentLayoutF.Slot1>
          <ContentLayoutF.Slot2>
            <Box css={{ position: 'sticky', top: '20px' }}>
              <ResolveUploadCsvForm onSubmit={handleUploadSubmit} uploadCsvValidationError={uploadCsvValidationError} />
            </Box>
          </ContentLayoutF.Slot2>
        </ContentLayoutF.SlotContainer>
      </ContentLayoutF>
    </Box>
  );
};
